// src/app/core/interceptors/request.interceptor.ts
import { inject, Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, EMPTY, throwError } from 'rxjs';
import { catchError, tap, switchMap, finalize, map, shareReplay, timeout } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { IonicService } from 'src/app/services/ionic.service';
import { Router } from '@angular/router';
import { UserStore } from 'src/app/store/user.store';
import { isTokenExpired } from '../utils/is-token-expired';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class RequestInterceptor implements HttpInterceptor {
  readonly UNSECURED_OPERATIONS = [
    '/auth/login',
    '/auth/register',
    '/auth/forgot-password',
    '/auth/confirm-forgot-password',
    '/auth/refresh-token',
    environment.urlImages,
    'maps.googleapis.com/',
    '/details'
  ];

  private readonly REFRESH_THRESHOLD_SEC = 61;
  private refreshRequest$: Observable<string> | null = null;

  userStore = inject(UserStore);

  constructor(
    private authService: AuthService,
    private ionicService: IonicService,
    private router: Router
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const persistedUser = JSON.parse(localStorage.getItem('user') || 'null');
    const token: string | undefined =
      this.userStore.user()?.tokenAuth ?? persistedUser?.tokenAuth;

    // Marcar sesión iniciada si el login fue exitoso
    if (request.url.includes('/auth/login') && request.method === 'POST') {
      return next.handle(request).pipe(
        tap((event: any) => {
          if (event?.body?.tokenAuth || event?.body?.token || event?.body?.accessToken) {
            localStorage.setItem('sessionStarted', 'true');
          }
        }),
        catchError((error) => throwError(() => error))
      );
    }

    if (this.isUnsecuredOperation(request)) {
      return next.handle(request);
    }

    if (token && !isTokenExpired(token) && !this.userStore.user()?.tokens) {
      this.userStore.updateUser(persistedUser);
    }

    // Si no hay token, continuar normal
    if (!token) {
      return next.handle(request);
    }

    // Si expiró: intentar refrescar usando refreshToken
    if (isTokenExpired(token)) {
      return this.refreshToken$().pipe(
        catchError(() => {
          this.handleSessionExpiredFlow();
          return EMPTY;
        }),
        switchMap((newToken) => next.handle(this.addAuthHeader(request, newToken)))
      );
    }

    // Si está por expirar: refrescar antes
    if (this.isAboutToExpire(token, this.REFRESH_THRESHOLD_SEC)) {
      console.log('Token expirará pronto. Refrescando...');
      return this.refreshToken$().pipe(
        catchError(() => {
          this.handleSessionExpiredFlow();
          return EMPTY;
        }),
        switchMap((newToken) => next.handle(this.addAuthHeader(request, newToken)))
      );
    }

    // Token válido: seguir
    request = this.addAuthHeader(request, token);

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          if (localStorage.getItem('sessionStarted') === 'true') {
            this.ionicService.showToast('Sesión expirada. Debes iniciar sesión nuevamente.', 'warning');
            this.ionicService.showAlert(
              'Acceso denegado',
              'Su sesión ha expirado. Inicie sesión nuevamente.',
              [{ text: 'Aceptar' }]
            );
          }
          this.authService.logout();
          this.router.navigate(['/']);
          localStorage.removeItem('sessionStarted');
          return EMPTY;
        }
        return throwError(() => error);
      }),
    );
  }

  private isUnsecuredOperation(request: HttpRequest<any>): boolean {
    return this.UNSECURED_OPERATIONS.some((operation) => request.url.includes(operation));
  }

  private addAuthHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
  }

  private isAboutToExpire(token: string, thresholdSec: number): boolean {
    console.log('Checking if token is about to expire...');
    const exp = this.getTokenExp(token);
    if (!exp) return false;
    const nowSec = Math.floor(Date.now() / 1000);
    console.log('Token expiration:', exp, 'Current time:', nowSec, 'Threshold:', exp - nowSec <= thresholdSec);
    // return true
    return exp - nowSec <= thresholdSec;
  }

  private getTokenExp(token: string): number | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return typeof payload?.exp === 'number' ? payload.exp : null;
    } catch {
      return null;
    }
  }

  /**
   * Refresca usando el refreshToken persistido.
   * - Encola las requests mientras se refresca.
   * - Actualiza access token y refresh token si el backend rota el refresh.
   */
  private refreshToken$(): Observable<string> {
    if (this.refreshRequest$) {
      return this.refreshRequest$;
    }

    // Obtener el refreshToken de store o localStorage
    const persistedUser = JSON.parse(localStorage.getItem('user') || 'null');
    const stored = this.userStore.user() ?? persistedUser;
    const currentRefreshToken: string | undefined = stored?.refreshToken;

    if (!currentRefreshToken) {
      return throwError(() => new Error('No hay refreshToken disponible'));
    }

    this.refreshRequest$ = this.authService.refreshToken(currentRefreshToken).pipe(
      timeout(30000),
      map((res: any) => {
        // Ajusta las propiedades según tu backend
        const newAccess = res?.tokenAuth ?? res?.token;
        const newRefresh = res?.refreshToken ?? currentRefreshToken;

        if (!newAccess) {
          throw new Error('No se recibió un token de acceso actualizado');
        }

        // Persistir ambos tokens
        const existing = this.userStore.user() ?? (persistedUser || {});
        const updated = { ...existing, tokenAuth: newAccess, refreshToken: newRefresh };
        localStorage.setItem('user', JSON.stringify(updated));
        this.userStore.updateUser(updated);

        localStorage.setItem('sessionStarted', 'true');
        return newAccess;
      }),
      finalize(() => (this.refreshRequest$ = null)),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this.refreshRequest$;
  }

  private handleSessionExpiredFlow() {
    if (localStorage.getItem('sessionStarted') === 'true') {
      this.ionicService.showToast('Sesión expirada. Debes iniciar sesión nuevamente.', 'warning');
      this.ionicService.showAlert(
        'Sesión expirada',
        'Su sesión ha expirado. Inicie sesión nuevamente para continuar operando',
        [{ text: 'Aceptar' }],
      );
    }
    this.authService.logout();
    localStorage.removeItem('sessionStarted');
    this.router.navigate(['/home']);
  }
}
